import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Table, Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { fetchClients } from '../../store/actions/client';

import { PLAN_LIMITS } from '../../config/plans';

import AddClient from './add';
import EditClient from './edit';
import { CLIENT_CATEGORIES } from './validationSchema';

import CustomModalLarge from '../../hoc/customModalLarge';

import PaymentAlert from '../../utils/paymentAlert';
import PlanLimitAlert from '../../utils/planLimitAlert';
import { isSubscriptionActive, shouldShowPaymentAlert } from '../../utils/tools';

// Assets
import { ReactComponent as EditIcon } from '../../assets/edit.svg';

const ShowClients = () => {
    const dispatch = useDispatch();
    const clients = useSelector(state => state.client.data);
    const currentClient = useSelector(state => state.client.current);
    const tenant = useSelector(state => state.tenant.current);

    const planLimits = PLAN_LIMITS[tenant.plan];
    const isActive = isSubscriptionActive(tenant?.subscriptionStatus, tenant?.plan);
    const isAtLimit = !!(tenant && Array.isArray(tenant.clients) && tenant.clients.length >= planLimits?.maxClients);

    // Custom modal state
    const [isAddModalVisible, setIsAddModalVisible] = useState(false);
    const [isEditModalVisible, setIsEditModalVisible] = useState(false);
    const [clientToEdit, setClientToEdit] = useState(null);

    // Handlers
    const handleAddClientButton = () => {
        setIsAddModalVisible(true);
    };

    const handleEditButtonClick = (client) => {
        setClientToEdit(client);
        setIsEditModalVisible(true);
    };

    const handleCloseAddModal = () => {
        setIsAddModalVisible(false);
    };

    const handleCloseEditModal = () => {
        setIsEditModalVisible(false);
        setClientToEdit(null);
    };

    useEffect(() => {
            dispatch(fetchClients());
    }, [dispatch, currentClient]);

    // Render Tooltips
    const renderTooltipEdit = (props) => (
        <Tooltip id="button-tooltip" {...props}>
            Upraviť odberateľa/dodávateľa
        </Tooltip>
    );


    return (
        <>
            <Button 
                onClick={handleAddClientButton}
                className="button-custom-large radius"
                disabled={tenant && Array.isArray(tenant.clients) && tenant.clients.length >= planLimits.maxClients || !isActive}
            >
                Pridať odberateľa/dodávateľa
            </Button>

            {tenant && Array.isArray(tenant.clients) && tenant.clients.length >= planLimits.maxClients && (
                <PlanLimitAlert
                    current={tenant.clients.length}
                    max={planLimits.maxClients}
                    label="odberateľov/dodávateľov"
                    plan={tenant.plan}
                />
            )}

            {tenant && !isAtLimit && shouldShowPaymentAlert(tenant.subscriptionStatus, tenant.plan) && (
                <PaymentAlert status={tenant.subscriptionStatus} plan={tenant.plan} />
            )}
            
            <div className="content pt-4 pb-4">
                <Table responsive hover>
                    <thead>
                        <tr>
                            <th>Názov spoločnosti</th>
                            <th>IČO</th>
                            <th>Mobil</th>
                            <th>Kategória</th>
                            <th>Akcia</th>
                        </tr>
                    </thead>
                    <tbody>
                        {Array.isArray(clients) && clients.length === 0 && (
                            <tr>
                                <td colSpan={5} className="text-center text-muted py-5">
                                    <p className="mb-1"><strong>Zatiaľ nemáte žiadneho odberateľa ani dodávateľa.</strong></p>
                                    <p className="mb-0">Kliknutím na tlačidlo <em>Pridať odberateľa/dodávateľa</em> vytvoríte prvý záznam.</p>
                                </td>
                            </tr>
                        )}
                        {Array.isArray(clients) && clients.map((client) => (
                            <tr key={client._id}>
                                <td>{client.companyName}</td>
                                <td>{client.identificationNumber}</td>
                                <td>{client.contact}</td>
                                <td>
                                    {CLIENT_CATEGORIES.filter(({ value }) => (client.categories || []).includes(value)).map(({ value, label }) => (
                                        <span key={value} className="badge bg-secondary me-1">{label}</span>
                                    ))}
                                </td>
                                <td>
                                    {/* Edit Icon */}
                                    <Button
                                        className="btnIcon"
                                    >
                                        <OverlayTrigger placement="top" overlay={renderTooltipEdit}>
                                            <EditIcon
                                                onClick={() => handleEditButtonClick(client)}
                                                alt="Upraviť"
                                                className="icon icon-edit"
                                            />
                                        </OverlayTrigger>
                                    </Button>

                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </div>

            {/* Custom modal for adding client */}
            <CustomModalLarge isVisible={isAddModalVisible} onClose={handleCloseAddModal}>
                <AddClient onClose={handleCloseAddModal} />
            </CustomModalLarge>

            {/* Custom modal for editing client */}
            <CustomModalLarge isVisible={isEditModalVisible} onClose={handleCloseEditModal}>
                { 
                    clientToEdit && isEditModalVisible ? 
                        <EditClient client={clientToEdit} onClose={handleCloseEditModal} />
                        : null
                }
            </CustomModalLarge>


        </>
    );
}

export default ShowClients;
